import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";
import "./ManageAdmins.css";

const ADMIN_PASS = (process.env.REACT_APP_ADMIN_PASSWORD || "").trim();

export default function ManageAdmins() {
  const [access, setAccess] = useState(false);
  const [password, setPassword] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setAuthChecking(false);
      if (!u) { setAccess(false); setUsers([]); setPassword(""); setShowSuccess(false); }
    });
    return () => unsub();
  }, []);

  const call = async (name, data = {}) => {
    const fn = httpsCallable(functions, name);
    const res = await fn({ adminPassword: ADMIN_PASS, ...data });
    return res?.data ?? res;
  };

  const handlePassword = async () => {
    const supplied = String(password || "").trim();
    if (!supplied) { alert("Enter admin password"); return; }
    if (!ADMIN_PASS) { alert("Missing REACT_APP_ADMIN_PASSWORD in .env.local (restart dev server)"); return; }
    if (!user) { alert("Sign in first"); return; }
    const token = await user.getIdTokenResult(true);
    const role = token?.claims?.role || "user";
    if (role !== "superadmin") { alert("Insufficient role"); return; }
    if (supplied !== ADMIN_PASS) { alert("Admin password incorrect"); return; }
    setShowSuccess(true);
  };

  const proceedAfterPopup = async () => {
    setShowSuccess(false);
    setAccess(true);
    await fetchUsers();
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const arr = await call("listUsersPublic");
      setUsers(Array.isArray(arr) ? arr : []);
    } catch (e) {
      alert(e.message || "Load failed");
    }
    setLoading(false);
  };

  const updateRole = async (email, role) => {
    try {
      await call("setUserRole", { email, role });
      await fetchUsers();
    } catch (e) {
      alert(e.message || "Update failed");
    }
  };

  const restart = () => {
    setAccess(false);
    setUsers([]);
    setPassword("");
    setShowSuccess(false);
  };

  const canEdit = access;

  if (authChecking) return (
    <div className="manage-admins">
      <div className="card"><h2>Checking your sign-in…</h2></div>
    </div>
  );

  if (!user) return (
    <div className="manage-admins">
      <div className="card"><h2>Sign in required</h2></div>
    </div>
  );

  if (!access) return (
    <div className="manage-admins">
      <div className="password-wrap card">
        <h2>Enter Admin Password</h2>
        <div className="row">
          <input
            className="input"
            type="password"
            value={password}
            onChange={(e)=>setPassword(e.target.value)}
            placeholder="Admin password"
          />
          <button className="AdminsBtn AdminsBtn-primary" onClick={handlePassword}>Submit</button>
        </div>

        {showSuccess ? (
          <div
            role="dialog"
            aria-modal="true"
            className="popup-overlay"
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
              display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
            }}
          >
            <div className="card" style={{ minWidth: 320, maxWidth: 420 }}>
              <h3>Password accepted</h3>
              <p>You’re verified as superadmin. Continue to Manage Admins?</p>
              <div className="row" style={{ gap: 8 }}>
                <button className="AdminsBtn btn-primary" onClick={proceedAfterPopup}>Continue</button>
                <button className="AdminsBtn" onClick={() => setShowSuccess(false)}>Cancel</button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  const rows = [];
  for (let i = 0; i < users.length; i++) {
    const u = users[i];
    rows.push(
      <tr key={u.email}>
        <td>{u.email}</td>
        <td>{u.role}</td>
        <td>
          {canEdit ? (
            u.role === "admin"
              ? <button className="AdminsBtn" onClick={() => updateRole(u.email, "user")}>Remove Admin</button>
              : u.role === "superadmin"
                ? <span>—</span>
                : <button className="AdminsBtn AdminsBtn-success" onClick={() => updateRole(u.email, "admin")}>Make Admin</button>
          ) : <span>—</span>}
        </td>
      </tr>
    );
  }

  return (
    <div className="manage-admins">
      <div className="card">
        <div className="controls" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2>Manage Admins</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="AdminsBtn" onClick={restart}>Restart</button>
            <button className="AdminsBtn AdminsBtn-primary" onClick={fetchUsers} disabled={loading}>
              {loading ? "Loading…" : "Reload"}
            </button>
          </div>
        </div>
        {loading ? <p>Loading users…</p> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Email</th><th>Role</th><th>Action</th></tr></thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
